import React, { useState } from 'react';
import { FileText, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ReportOverview = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    reportTitle: initialData.reportTitle || '',
    reportingPeriod: initialData.reportingPeriod || '',
    companyName: initialData.companyName || '',
    reportType: initialData.reportType || 'sustainability',
    sections: initialData.sections || {
      executiveSummary: true,
      methodology: true,
      dataCollection: true,
      calculations: true,
      materialityAnalysis: true,
      esrsDisclosures: true,
      appendices: true
    }
  });

  const [validationErrors, setValidationErrors] = useState({});

  const reportTypes = [
    { value: 'sustainability', label: t('workflow.draftReport.reportOverview.reportTypes.sustainability') },
    { value: 'esg', label: t('workflow.draftReport.reportOverview.reportTypes.esg') },
    { value: 'carbon', label: t('workflow.draftReport.reportOverview.reportTypes.carbon') },
    { value: 'esrs', label: t('workflow.draftReport.reportOverview.reportTypes.esrs') }
  ];

  const sectionOptions = [
    { key: 'executiveSummary', label: t('workflow.draftReport.reportOverview.sections.executiveSummary.label'), description: t('workflow.draftReport.reportOverview.sections.executiveSummary.description') },
    { key: 'methodology', label: t('workflow.draftReport.reportOverview.sections.methodology.label'), description: t('workflow.draftReport.reportOverview.sections.methodology.description') },
    { key: 'dataCollection', label: t('workflow.draftReport.reportOverview.sections.dataCollection.label'), description: t('workflow.draftReport.reportOverview.sections.dataCollection.description') },
    { key: 'calculations', label: t('workflow.draftReport.reportOverview.sections.calculations.label'), description: t('workflow.draftReport.reportOverview.sections.calculations.description') },
    { key: 'materialityAnalysis', label: t('workflow.draftReport.reportOverview.sections.materialityAnalysis.label'), description: t('workflow.draftReport.reportOverview.sections.materialityAnalysis.description') },
    { key: 'esrsDisclosures', label: t('workflow.draftReport.reportOverview.sections.esrsDisclosures.label'), description: t('workflow.draftReport.reportOverview.sections.esrsDisclosures.description') },
    { key: 'appendices', label: t('workflow.draftReport.reportOverview.sections.appendices.label'), description: t('workflow.draftReport.reportOverview.sections.appendices.description') }
  ];

  const validateForm = () => {
    const errors = {};

    if (!formData.reportTitle.trim()) {
      errors.reportTitle = t('workflow.draftReport.reportOverview.validation.reportTitleRequired');
    }

    if (!formData.reportingPeriod.trim()) {
      errors.reportingPeriod = t('workflow.draftReport.reportOverview.validation.reportingPeriodRequired');
    }

    if (!formData.companyName.trim()) {
      errors.companyName = t('workflow.draftReport.reportOverview.validation.companyNameRequired');
    }

    if (!formData.reportType) {
      errors.reportType = t('workflow.draftReport.reportOverview.validation.reportTypeRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleSectionToggle = (sectionKey) => {
    setFormData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: !prev.sections[sectionKey]
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center mb-6">
          <FileText className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{t('workflow.draftReport.reportOverview.title')}</h2>
            <p className="text-gray-600">{t('workflow.draftReport.reportOverview.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.reportOverview.reportTitle')} *
              </label>
              <input
                type="text"
                value={formData.reportTitle}
                onChange={(e) => handleInputChange('reportTitle', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.reportTitle ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('workflow.draftReport.reportOverview.reportTitlePlaceholder')}
              />
              {validationErrors.reportTitle && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.reportTitle}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.reportOverview.companyName')} *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.companyName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('workflow.draftReport.reportOverview.companyNamePlaceholder')}
              />
              {validationErrors.companyName && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.reportOverview.reportingPeriod')} *
              </label>
              <input
                type="text"
                value={formData.reportingPeriod}
                onChange={(e) => handleInputChange('reportingPeriod', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.reportingPeriod ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('workflow.draftReport.reportOverview.reportingPeriodPlaceholder')}
              />
              {validationErrors.reportingPeriod && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.reportingPeriod}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('workflow.draftReport.reportOverview.reportType')} *
              </label>
              <select
                value={formData.reportType}
                onChange={(e) => handleInputChange('reportType', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.reportType ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('workflow.draftReport.reportOverview.reportTypePlaceholder')}</option>
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {validationErrors.reportType && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.reportType}</p>
              )}
            </div>
          </div>

          {/* Report Sections */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('workflow.draftReport.reportOverview.reportSections')}</h3>
            <p className="text-gray-600 mb-4">{t('workflow.draftReport.reportOverview.reportSectionsDescription')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sectionOptions.map(section => (
                <div
                  key={section.key}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.sections[section.key]
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => handleSectionToggle(section.key)}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {formData.sections[section.key] ? (
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                      )}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-800">{section.label}</h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">{t('workflow.draftReport.reportOverview.reportSummary')}</h4>
            <div className="text-sm text-gray-600">
              <p><strong>{t('workflow.draftReport.reportOverview.reportTitle')}:</strong> {formData.reportTitle || t('workflow.draftReport.reportOverview.notSpecified')}</p>
              <p><strong>{t('workflow.draftReport.reportOverview.companyName')}:</strong> {formData.companyName || t('workflow.draftReport.reportOverview.notSpecified')}</p>
              <p><strong>{t('workflow.draftReport.reportOverview.reportingPeriod')}:</strong> {formData.reportingPeriod || t('workflow.draftReport.reportOverview.notSpecified')}</p>
              <p><strong>{t('workflow.draftReport.reportOverview.reportType')}:</strong> {reportTypes.find(t => t.value === formData.reportType)?.label || t('workflow.draftReport.reportOverview.notSpecified')}</p>
              <p><strong>{t('workflow.draftReport.reportOverview.reportSections')}:</strong> {Object.values(formData.sections).filter(Boolean).length} {t('workflow.draftReport.reportOverview.sectionsSelected')}</p>
            </div>
          </div>


        </form>
      </div>
    </div>
  );
};

export default ReportOverview;
